import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, LinearProgress, MenuItem, Paper,
  Select, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import { supabase } from '../config/supabase';

type Team = { id: string; team_name: string; league_name?: string | null };
type Settings = {
  botId: string | null; botName: string | null;
  botStatus: 'unconfigured' | 'connected' | 'missing' | 'multiple' | 'invalid';
  botValidatedAt: string | null; botValidationError: string | null;
  scheduleEnabled: boolean; schedulePinEnabled: boolean; scheduleDayOfWeek: number; scheduleTime: string; scheduleLookaheadDays: number;
  lineupMode: 'disabled' | 'manual_only' | 'scheduled'; lineupLookaheadDays: number;
  lineupScheduleDayOfWeek: number; lineupScheduleTime: string; timezone: string;
};
type Preview = { type: 'upcoming_schedule' | 'upcoming_lineup'; message?: string; noContent?: boolean; unchanged?: boolean; warning?: string; matchId?: string };
type Match = { id: string; opponent_name: string | null; match_date: string; lineup_status: string };
type Snack = { severity: 'success' | 'error' | 'info'; message: string };
const scheduledTimeOptions = Array.from({ length: 24 * 4 }, (_value, index) => {
  const hours = Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  const hour12 = hours % 12 || 12;
  return {
    value: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
    label: `${hour12}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`,
  };
});

function TimeSelect({ value, onChange, disabled = false }: { value: string; onChange: (value: string) => void; disabled?: boolean }) {
  const isValidValue = scheduledTimeOptions.some((option) => option.value === value);
  return <FormControl size="small" disabled={disabled} sx={{ minWidth: 132 }}>
    <InputLabel>Time</InputLabel>
    <Select label="Time" value={value} onChange={(event) => onChange(String(event.target.value))}>
      {!isValidValue && <MenuItem value={value} disabled>{value} (choose a quarter hour)</MenuItem>}
      {scheduledTimeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
    </Select>
  </FormControl>;
}

function sameSettings(left: Settings | null, right: Settings | null): boolean {
  if (!left || !right) return left === right;
  return left.scheduleEnabled === right.scheduleEnabled
    && left.schedulePinEnabled === right.schedulePinEnabled
    && left.scheduleDayOfWeek === right.scheduleDayOfWeek
    && left.scheduleTime === right.scheduleTime
    && left.scheduleLookaheadDays === right.scheduleLookaheadDays
    && left.lineupMode === right.lineupMode
    && left.lineupLookaheadDays === right.lineupLookaheadDays
    && left.lineupScheduleDayOfWeek === right.lineupScheduleDayOfWeek
    && left.lineupScheduleTime === right.lineupScheduleTime;
}

async function authHeaders(json = false): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(data.session?.access_token ? { Authorization: `Bearer ${data.session.access_token}` } : {}),
  };
}

export function GroupMeTeamUpdates({ teams, defaultCaptainTeamIds, writableTeamIds, onConfigureGroupMe }: {
  teams: Team[];
  defaultCaptainTeamIds: string[];
  writableTeamIds: string[];
  onConfigureGroupMe?: (teamId: string) => void;
}) {
  const [teamId, setTeamId] = useState('');
  const [saved, setSaved] = useState<Settings | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchId, setMatchId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [pendingTeamId, setPendingTeamId] = useState<string | null>(null);
  const [snack, setSnack] = useState<Snack | null>(null);
  const dirty = !sameSettings(saved, settings);
  const botReady = settings?.botStatus === 'connected';
  const manageableTeams = useMemo(() => teams.filter((team) => writableTeamIds.includes(team.id)), [teams, writableTeamIds]);
  useEffect(() => {
    setTeamId((current) => {
      if (current && manageableTeams.some((team) => team.id === current)) return current;
      return manageableTeams.find((team) => defaultCaptainTeamIds.includes(team.id))?.id || '';
    });
  }, [defaultCaptainTeamIds, manageableTeams]);
  const canConfigure = Boolean(teamId && writableTeamIds.includes(teamId) && onConfigureGroupMe);
  const scheduleTimeValid = Boolean(settings && scheduledTimeOptions.some((option) => option.value === settings.scheduleTime));
  const lineupTimeValid = Boolean(settings && scheduledTimeOptions.some((option) => option.value === settings.lineupScheduleTime));

  const load = useCallback(async (nextTeamId: string) => {
    if (!nextTeamId) { setSaved(null); setSettings(null); setMatches([]); setMatchId(''); return; }
    setLoading(true);
    const [statusResponse, matchesResponse] = await Promise.all([
      fetch(`/api/groupme/team-updates/status?teamId=${encodeURIComponent(nextTeamId)}`, { headers: await authHeaders() }),
      supabase.from('match_events').select('id, opponent_name, match_date, lineup_status')
        .eq('team_id', nextTeamId).eq('lineup_status', 'Published')
        .in('match_status', ['Scheduled', 'Rescheduled', 'Postponed']).is('deleted_at', null)
        .order('match_date'),
    ]);
    const result = await statusResponse.json() as { settings?: Settings; error?: string };
    setLoading(false);
    if (!statusResponse.ok) {
      setSaved(null); setSettings(null); setMatches([]); setMatchId('');
      setSnack({ severity: 'error', message: result.error || 'Unable to load GroupMe Updates.' });
      return;
    }
    const nextSettings = result.settings || null;
    setSaved(nextSettings); setSettings(nextSettings);
    setMatches((matchesResponse.data || []) as Match[]); setMatchId('');
  }, []);

  useEffect(() => { void load(teamId); }, [load, teamId]);

  const requestTeamChange = (nextTeamId: string) => {
    if (nextTeamId === teamId) return;
    if (dirty) { setPendingTeamId(nextTeamId); return; }
    setTeamId(nextTeamId);
  };
  const discardAndSwitch = () => { if (pendingTeamId !== null) setTeamId(pendingTeamId); setPendingTeamId(null); };
  const update = (patch: Partial<Settings>) => setSettings((current) => current ? { ...current, ...patch } : current);
  const valid = Boolean(settings && settings.scheduleLookaheadDays >= 1 && settings.scheduleLookaheadDays <= 60 && settings.lineupLookaheadDays >= 1 && settings.lineupLookaheadDays <= 60 && scheduleTimeValid && lineupTimeValid);

  const save = async () => {
    if (!teamId || !settings || !botReady || !dirty || !valid) return;
    setSaving(true);
    const response = await fetch('/api/groupme/team-updates/save', { method: 'POST', headers: await authHeaders(true), body: JSON.stringify({ teamId, ...settings }) });
    const result = await response.json() as { settings?: Settings; error?: string };
    setSaving(false);
    if (!response.ok) { setSnack({ severity: 'error', message: result.error || 'Unable to save GroupMe update settings.' }); return; }
    setSaved(result.settings || settings); setSettings(result.settings || settings);
    setSnack({ severity: 'success', message: 'GroupMe update settings saved.' });
  };
  const prepare = async (type: Preview['type']) => {
    if (!teamId || !botReady || dirty || (type === 'upcoming_lineup' && !matchId)) return;
    const response = await fetch('/api/groupme/team-updates/preview', { method: 'POST', headers: await authHeaders(true), body: JSON.stringify({ teamId, updateType: type, matchId: type === 'upcoming_lineup' ? matchId : undefined }) });
    const result = await response.json() as Preview & { error?: string };
    if (!response.ok) { setSnack({ severity: 'error', message: result.error || 'Unable to prepare GroupMe preview.' }); return; }
    setPreview({ ...result, matchId: type === 'upcoming_lineup' ? matchId : undefined });
  };
  const send = async (confirmUnchanged = false) => {
    if (!teamId || !preview) return;
    setSaving(true);
    const response = await fetch('/api/groupme/team-updates/send', { method: 'POST', headers: await authHeaders(true), body: JSON.stringify({ teamId, updateType: preview.type, matchId: preview.matchId, confirmUnchanged }) });
    const result = await response.json() as { confirmationRequired?: boolean; warning?: string; message?: string; error?: string };
    setSaving(false);
    if (result.confirmationRequired) { setPreview((current) => current ? { ...current, warning: result.warning } : current); return; }
    if (!response.ok) { setSnack({ severity: 'error', message: result.error || 'Unable to send GroupMe update.' }); return; }
    setPreview(null); setSnack({ severity: 'success', message: result.message || 'GroupMe update sent.' });
  };


  return <Stack spacing={2}>
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>GroupMe Updates</Typography>
        <FormControl fullWidth sx={{ maxWidth: 520 }}>
          <InputLabel>Team</InputLabel>
          <Select label="Team" value={teamId} onChange={(event) => requestTeamChange(String(event.target.value))}>
            {manageableTeams.map((team) => <MenuItem key={team.id} value={team.id}>{team.team_name}{team.league_name ? ` · ${team.league_name}` : ''}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
    </Paper>

    {loading && <LinearProgress />}
    {!teamId && !loading && <Alert severity="info">{manageableTeams.length ? 'Select a team to manage its GroupMe updates.' : 'You do not have permission to manage GroupMe updates for a team.'}</Alert>}
    {teamId && !loading && !botReady && <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Alert severity="info"><Typography sx={{ fontWeight: 700 }}>GroupMe bot not configured</Typography>This team does not have a configured and validated GroupMe bot. Configure a GroupMe bot before using scheduled or on-demand GroupMe updates.{settings?.botValidationError ? ` ${settings.botValidationError}` : ''}</Alert>
        {canConfigure && <Button startIcon={<SettingsIcon />} variant="outlined" sx={{ alignSelf: 'flex-start' }} onClick={() => onConfigureGroupMe?.(teamId)}>Configure GroupMe Bot</Button>}
      </Stack>
    </Paper>}
    {teamId && !loading && botReady && settings && <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Typography variant="caption" color={dirty ? 'warning.main' : 'text.secondary'} sx={{ fontWeight: dirty ? 700 : 400 }}>{dirty ? 'Unsaved changes' : 'Up to date'} · {settings.botName || 'GroupMe bot'} · {settings.timezone}</Typography>
        <Button variant="contained" disabled={!dirty || !valid || saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save Changes'}</Button>
      </Stack>
      {dirty && <Alert severity="info">Save changes before sending an update.</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2 }}><Stack spacing={1.5}>
          <Box><Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Upcoming Schedule</Typography><Typography variant="body2" color="text.secondary">Keep players informed about upcoming team events.</Typography></Box>
          <FormControlLabel control={<Checkbox checked={settings.scheduleEnabled} onChange={(event) => update({ scheduleEnabled: event.target.checked })} />} label="Scheduled" />
          <FormControlLabel control={<Checkbox checked={settings.schedulePinEnabled} onChange={(event) => update({ schedulePinEnabled: event.target.checked })} />} label="Pin Schedule Post" />
          <Typography variant="caption" color="text.secondary">Pins the latest CourtCaptain schedule in GroupMe and replaces the previous CourtCaptain schedule pin when the next schedule is published.</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(150px, 1fr) minmax(132px, 0.8fr) minmax(150px, 1fr)' }, gap: 1 }}>
            <FormControl size="small"><InputLabel>Day</InputLabel><Select label="Day" value={settings.scheduleDayOfWeek} onChange={(event) => update({ scheduleDayOfWeek: Number(event.target.value) })}>{['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => <MenuItem key={day} value={index}>{day}</MenuItem>)}</Select></FormControl>
            <TimeSelect value={settings.scheduleTime} onChange={(scheduleTime) => update({ scheduleTime })} />
            <TextField size="small" label="Look Ahead Days" type="number" inputProps={{ min: 1, max: 60 }} value={settings.scheduleLookaheadDays} onChange={(event) => update({ scheduleLookaheadDays: Number(event.target.value) })} />
          </Box>
          <Button startIcon={<SendIcon />} variant="outlined" sx={{ alignSelf: 'flex-start' }} disabled={dirty || saving} onClick={() => void prepare('upcoming_schedule')}>Send Schedule Now</Button>
        </Stack></Paper>
        <Paper variant="outlined" sx={{ p: 2 }}><Stack spacing={1.5}>
          <Box><Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Upcoming Lineups</Typography><Typography variant="body2" color="text.secondary">Publish upcoming Published lineups to the team&apos;s GroupMe.</Typography></Box>
          <FormControlLabel control={<Checkbox checked={settings.lineupMode === 'scheduled'} onChange={(event) => update({ lineupMode: event.target.checked ? 'scheduled' : 'manual_only' })} />} label="Scheduled" />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(150px, 1fr) minmax(132px, 0.8fr) minmax(150px, 1fr)' }, gap: 1 }}>
            <FormControl size="small" disabled={settings.lineupMode !== 'scheduled'}><InputLabel>Day</InputLabel><Select label="Day" value={settings.lineupScheduleDayOfWeek} onChange={(event) => update({ lineupScheduleDayOfWeek: Number(event.target.value) })}>{['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => <MenuItem key={day} value={index}>{day}</MenuItem>)}</Select></FormControl>
            <TimeSelect value={settings.lineupScheduleTime} disabled={settings.lineupMode !== 'scheduled'} onChange={(lineupScheduleTime) => update({ lineupScheduleTime })} />
            <TextField size="small" label="Look Ahead Days" type="number" inputProps={{ min: 1, max: 60 }} value={settings.lineupLookaheadDays} disabled={settings.lineupMode !== 'scheduled'} onChange={(event) => update({ lineupLookaheadDays: Number(event.target.value) })} />
          </Box>
          <FormControl size="small" disabled={settings.lineupMode === 'disabled'}><InputLabel>Eligible Match</InputLabel><Select label="Eligible Match" value={matchId} onChange={(event) => setMatchId(String(event.target.value))}><MenuItem value=""><em>{matches.length ? 'Choose match' : 'No eligible matches'}</em></MenuItem>{matches.map((match) => <MenuItem key={match.id} value={match.id}>{match.match_date} · vs {match.opponent_name || 'TBD'} · {match.lineup_status}</MenuItem>)}</Select></FormControl>
          <Button startIcon={<SendIcon />} variant="outlined" sx={{ alignSelf: 'flex-start' }} disabled={dirty || saving || settings.lineupMode === 'disabled' || !matchId} onClick={() => void prepare('upcoming_lineup')}>Publish Lineup to GroupMe</Button>
        </Stack></Paper>
      </Box>
    </Stack>}
    <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} fullWidth maxWidth="sm">
      <DialogTitle>Preview GroupMe Update</DialogTitle><DialogContent><Stack spacing={1.5}>{preview?.noContent ? <Alert severity="info">No upcoming player-visible events were found.</Alert> : <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>{preview?.message}</Typography>}{preview?.warning && <Alert severity="warning">{preview.warning}</Alert>}</Stack></DialogContent>
      <DialogActions><Button onClick={() => setPreview(null)}>Cancel</Button>{!preview?.noContent && <Button variant="contained" disabled={saving} onClick={() => void send(Boolean(preview?.warning))}>{saving ? 'Sending…' : preview?.warning ? 'Send Again' : 'Send'}</Button>}</DialogActions>
    </Dialog>
    <Dialog open={pendingTeamId !== null} onClose={() => setPendingTeamId(null)}><DialogTitle>Unsaved changes</DialogTitle><DialogContent>You have unsaved changes. Discard them and switch teams?</DialogContent><DialogActions><Button onClick={() => setPendingTeamId(null)}>Stay</Button><Button color="warning" onClick={discardAndSwitch}>Discard and Switch</Button></DialogActions></Dialog>
    <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>{snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}</Snackbar>
  </Stack>;
}
